import React, { useState, useEffect } from 'react';
import tw from "twin.macro";
import styled from 'styled-components';

const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%; /* Adjusted to take full width */
  max-width: 450px; /* Added max-width for responsiveness */
  height: 100%;
  max-height: 540px; /* Added max-height for responsiveness */
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  // margin-top: 6px; /* Added margin for spacing */
`;

const Subheading = styled.div`
  ${tw`font-bold text-2xl mt-2`} /* Adjusted font size */
  color: #2862ff;
`;

const IndicatorRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 350px; /* Adjusted max-width for responsiveness */
  height: 100%;
  max-height: 450px; /* Added max-height for responsiveness */
  position: relative;
`;

const IndicatorHolder = styled.div`
  position: relative;
`;

const IndicatorBase = styled.img`
  width: 100%; /* Adjusted width to be responsive */
  height: 100%; /* Adjusted to auto for responsive height */
`;

const IndicatorPart = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%; /* Adjusted width to be responsive */
  height: 100%; /* Adjusted to auto for responsive height */
  transition: opacity 0.3s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const ArrowHolder = styled.div`
  position: absolute;
  top: 56%; /* Center vertically */
  left: 52%; /* Center horizontally */
  transform: translate(-50%, -50%); /* Center the element */
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  display: flex;
  justify-content: center;
  align-items: center;
`;


const SvgArrow = styled.svg`
  transform: ${({ angle }) => `rotate(${angle}deg)`};
  transition: transform 0.3s;
`;

const MMIValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  // margin-top: -10px; /* Adjusted margin for spacing */
`;

const Number = styled.span`
  font-size: 1.2em; /* Adjusted font size */
  font-weight: bold;
`;

const Date = styled.p`
  font-size: 0.8em; /* Adjusted font size */
  color: gray;
`;

const TooltipHolder = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Tooltip = styled.div`
  visibility: hidden;
  width: 100%; /* Adjusted width to be responsive */
  max-width: 200px; /* Added max-width for responsiveness */
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 5px;
  padding: 10px;
  position: absolute;
  z-index: 1;
  bottom: 110%;
  left: 50%;
  margin-left: -100px;
  opacity: 0;
  transition: opacity 0.3s;

  ${TooltipHolder}:hover & {
    visibility: visible;
    opacity: 1;
  }
`;

const RefreshButton = styled.button`
  margin-top: 8px; /* Adjusted margin for spacing */
  margin-bottom: 5px; /* Adjusted margin for spacing */
  padding: 6px 8px; /* Adjusted padding for smaller size */
  font-size: 0.7em; /* Adjusted font size */
  color: #fff;
  background-color: #2862ff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const zones = [
  { className: "ef", text: "High extreme fear (<20) suggests a good time to open fresh positions, as markets are likely to be oversold and might turn upwards" },
  { className: "fear", text: "Fear zone suggests that investors are fearful in the market, but the action to be taken depends on the MMI trajectory. See all zones for details" },
  { className: "greed", text: "Greed zone suggests that investors are acting greedy in the market, but the action to be taken depends on the MMI trajectory. See all zones for details" },
  { className: "eg", text: "High extreme greed (>80) suggests investors should avoid opening fresh positions as markets are overbought and likely to turn downwards" },
];

const getZoneImage = (value) => {
  if (value <= 20) return "https://www.tickertape.in/images/svg/mmi/indicator_parts/ef.svg";
  if (value <= 45) return "https://www.tickertape.in/images/svg/mmi/indicator_parts/fear.svg";
  if (value <= 70) return "https://www.tickertape.in/images/svg/mmi/indicator_parts/greed.svg";
  return "https://www.tickertape.in/images/svg/mmi/indicator_parts/eg.svg";
};

const calculateRotationAngle = (value) => {
  // Map value range [0, 100] to angle range [-90, 90]
  return (value - 34.6) * 1.6;
};

const MarketMoodIndex = () => {
  const [mmiValue, setMMIValue] = useState(null);
  const [dateUpdated, setDateUpdated] = useState(null);

  const fetchMMIData = async () => {
    try {
      const response = await fetch('https://falcon-algo.onrender.com/mmi');
      const data = await response.json();
      if (data && data.success) {
        setMMIValue(data.data.indicator);
        setDateUpdated(new Date(data.data.date).toLocaleString());
      }
    } catch (error) {
      console.error('Error fetching the MMI data:', error);
    }
  };

  useEffect(() => {
    fetchMMIData();
  }, []);

  const zoneImage = mmiValue !== null ? getZoneImage(mmiValue) : "";
  const rotationAngle = mmiValue !== null ? calculateRotationAngle(mmiValue) : 0;

  return (
    <WidgetContainer>
      <Subheading>Market Mood Index</Subheading>
      <IndicatorRoot>
        <IndicatorHolder>
          <IndicatorBase src="https://www.tickertape.in/images/svg/mmi/indicator-base.svg" alt="MMI" />
          {zones.map((zone, index) => (
            <IndicatorPart
              key={index}
              src={zoneImage}
              alt={zone.className}
              className={zone.className}
            />
          ))}
          <ArrowHolder>
            <SvgArrow angle={rotationAngle} viewBox="0 0 330 330" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(160 83)" fill="none" fillRule="evenodd">
                <path d="M5.03649827,0.963977754 L5.03649827,0.963977754 C5.59328914,0.963977754 6.05130089,1.40250865 6.07548634,1.95877399 L9.81504821,87.968697 C9.92989495,90.6101721 7.88165793,92.8446125 5.24018276,92.9594593 C5.17090752,92.9624712 5.10157502,92.9639778 5.03223433,92.9639778 L5.03223433,92.9639778 C2.38826367,92.9639778 0.244901967,90.820616 0.244901967,88.1766454 C0.244901967,88.1073047 0.246408484,88.0379722 0.249420451,87.968697 L3.98861189,1.96729383 C4.01300447,1.40626441 4.47493882,0.963977754 5.03649827,0.963977754 Z" fill="#BDC3CB" />
              </g>
            </SvgArrow>
          </ArrowHolder>
        </IndicatorHolder>
        {mmiValue !== null && (
          <MMIValue className="mmi-value">
            <Number className="number">{mmiValue.toFixed(2)}</Number>
            <Date className="typography-body-regular-xs text-teritiary date">Updated {dateUpdated}</Date>
          </MMIValue>
        )}
        {zones.map((zone, index) => (
          <TooltipHolder key={index} className={zone.className}>
            <Tooltip>{zone.text}</Tooltip>
          </TooltipHolder>
        ))}
        <RefreshButton onClick={fetchMMIData}>REFRESH</RefreshButton>
      </IndicatorRoot>
    </WidgetContainer>
  );
};

export default MarketMoodIndex;
